import type { IStoryblokStorySeo, IStoryblokStoryHreflangTag } from '~ui/types/seo/StoryblokAPI';
import type { RouteLocationNormalizedLoaded } from 'vue-router';

export function setSeoMeta(
    seoMetaTags: IStoryblokStorySeo[],
    config: { STORYBLOK_VERSION: string; STORYBLOK_FOLDER: string },
    route: RouteLocationNormalizedLoaded
) {
    if (!seoMetaTags) {
        return;
    }

    const siteUrl = useRuntimeConfig().public?.SITE_URL;
    const hreflangTags = generateHreflangLinks(seoMetaTags[0]?.hreflang_tags || [], siteUrl, route);

    useHead({
        link: [
            {
                rel: 'canonical',
                href: seoMetaTags[0]?.canonical || siteUrl + route.path,
            },
            ...hreflangTags,
        ],
    });

    useSeoMeta({
        title: () => seoMetaTags[0]?.title ?? '',
        description: () => seoMetaTags[0]?.description ?? '',
        robots: () => (config.STORYBLOK_VERSION === 'published' ? seoMetaTags[0]?.robots : 'nofollow,noindex'),
        ogTitle: () => seoMetaTags[0]?.title ?? '',
        ogDescription: () => seoMetaTags[0]?.description ?? '',
        ogImage: () => seoMetaTags[0]?.og_image?.filename ?? '',
        ogType: () => seoMetaTags[0]?.og_type ?? '',
        ogUrl: () => seoMetaTags[0]?.og_url ?? '',
        twitterCard: () => seoMetaTags[0]?.twitter_card ?? '',
        twitterSite: () => seoMetaTags[0]?.twitter_site ?? '',
        twitterTitle: () => seoMetaTags[0]?.twitter_title ?? '',
        twitterDescription: () => seoMetaTags[0]?.twitter_description ?? '',
        twitterCreator: () => seoMetaTags[0]?.twitter_creator ?? '',
        twitterImage: () => seoMetaTags[0]?.twitter_image?.filename ?? '',
    });
}

export function generateHreflangLinks(
    hreflangTagsRaw: IStoryblokStoryHreflangTag[] = [],
    siteUrl: string,
    route: RouteLocationNormalizedLoaded
): { rel: string; hreflang: string; href: string }[] {
    const currentLang = getLangFromRoutePath(route);

    const xDefaultTag = hreflangTagsRaw.find((tag) => tag.x_default);
    const tags = hreflangTagsRaw.filter((tag) => {
        if (tag.x_default) return false;
        const lang = tag.language === 'default' ? 'uk' : tag.language;
        return lang !== currentLang;
    });

    const links = tags.map((tag) => ({
        rel: 'alternate',
        hreflang: tag.language === 'default' ? 'uk' : tag.language,
        href: `${siteUrl}/${tag.slug.replace(/^\/+/, '')}`,
    }));

    // Self-referencing link
    if (tags.length > 0 && currentLang) {
        links.push({
            rel: 'alternate',
            hreflang: currentLang,
            href: `${siteUrl}${route.path}`,
        });

        // x-default logic
        const xDefaultHref =
            xDefaultTag?.slug || tags.find((t) => t.language === 'default')?.slug || route.path.replace(/^\/+/, '');

        links.push({
            rel: 'alternate',
            hreflang: 'x-default',
            href: `${siteUrl}/${xDefaultHref}`.replace(/\/+$/, ''),
        });
    }

    return links;
}

function getLangFromRoutePath(route: RouteLocationNormalizedLoaded): string {
    // Extract the first path segment (assumed to be the current locale)
    return route.path.replace(/^\/+/, '').split('/')[0] || '';
}
